/**
 * IP Detection Utility for Rate Limiting
 * Stage 4: Backend-Frontend Integration
 */

export class IPDetectionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'IPDetectionError';
  }
}

/**
 * Get user's IP address for rate limiting
 * Handles proxy and CDN scenarios with fallback mechanisms
 */
export async function getUserIP(): Promise<string> {
  const fallbackIP = '127.0.0.1'; // Fallback for development
  
  try {
    // CHECK FOR DEVELOPMENT FIRST - before trying external APIs
    if (process.env.NODE_ENV === 'development' || 
        window.location.hostname === 'localhost' || 
        window.location.hostname === '127.0.0.1') {
      // console.log('Development environment detected, using local IP:', fallbackIP);
      return fallbackIP;
    }

    // Method 1: Try ipify API (most reliable)
    try {
      const response = await fetch('https://api.ipify.org?format=json', {
        method: 'GET',
        timeout: 5000,
      } as any);
      
      if (response.ok) {
        const data = await response.json();
        if (data.ip && isValidIP(data.ip)) {
          // console.log('IP detected via ipify:', data.ip);
          return data.ip;
        }
      }
    } catch (error) {
      console.warn('ipify API failed:', error);
    }

    // Method 2: Try ipapi.co (backup)
    try {
      const response = await fetch('https://ipapi.co/ip/', {
        method: 'GET',
        timeout: 5000,
      } as any);
      
      if (response.ok) {
        const ip = await response.text();
        if (ip && isValidIP(ip.trim())) {
          // console.log('IP detected via ipapi.co:', ip.trim());
          return ip.trim();
        }
      }
    } catch (error) {
      console.warn('ipapi.co failed:', error);
    }

    // Method 3: Try httpbin.org (another backup)
    try {
      const response = await fetch('https://httpbin.org/ip', {
        method: 'GET',
        timeout: 5000,
      } as any);
      
      if (response.ok) {
        const data = await response.json();
        if (data.origin && isValidIP(data.origin)) {
          // console.log('IP detected via httpbin:', data.origin);
          return data.origin;
        }
      }
    } catch (error) {
      console.warn('httpbin.org failed:', error);
    }

    throw new IPDetectionError('All IP detection methods failed');
    
  } catch (error) {
    console.error('IP detection failed:', error);
    
    // Final fallback for development
    if (process.env.NODE_ENV === 'development') {
      // console.log('Using development fallback IP:', fallbackIP);
      return fallbackIP;
    }
    
    throw new IPDetectionError(`Failed to detect IP address: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Validate IP address format
 */
function isValidIP(ip: string): boolean {
  // IPv4 validation
  const ipv4Regex = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;
  const ipv4Match = ip.match(ipv4Regex);
  
  if (ipv4Match) {
    const octets = ipv4Match.slice(1).map(Number);
    return octets.every(octet => octet >= 0 && octet <= 255);
  }
  
  // IPv6 validation (basic)
  const ipv6Regex = /^([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$/;
  if (ipv6Regex.test(ip)) {
    return true;
  }
  
  // IPv6 compressed format (basic check)
  if (ip.includes('::') && ip.split('::').length === 2) {
    return true;
  }
  
  return false;
}

/**
 * Get cached IP with expiration
 */
const IP_CACHE_KEY = 'user_ip_cache';
const IP_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export async function getCachedUserIP(): Promise<string> {
  try {
    const cached = localStorage.getItem(IP_CACHE_KEY);
    if (cached) {
      const { ip, timestamp } = JSON.parse(cached);
      const now = Date.now();
      
      if (now - timestamp < IP_CACHE_DURATION && isValidIP(ip)) {
        // console.log('Using cached IP:', ip);
        return ip;
      }
    }
  } catch (error) {
    console.warn('Failed to read IP cache:', error);
  }
  
  // Get fresh IP and cache it
  const ip = await getUserIP();
  
  try {
    localStorage.setItem(IP_CACHE_KEY, JSON.stringify({
      ip,
      timestamp: Date.now()
    }));
  } catch (error) {
    console.warn('Failed to cache IP:', error);
  }
  
  return ip;
}

/**
 * Clear IP cache (useful for testing)
 */
export function clearIPCache(): void {
  try {
    localStorage.removeItem(IP_CACHE_KEY);
    // console.log('IP cache cleared');
  } catch (error) {
    console.warn('Failed to clear IP cache:', error);
  }
}
